import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import './splash.css';

// ── Content (curated from the club's existing site) ─────────────────
const CLASSES = [
  {
    tag: 'Most Popular',
    title: 'Puppy Classes',
    interest: 'Puppy',
    photo: '/img/class-puppy.jpg',
    body: 'Our most popular program — an 8-week foundation in sit, stay, heel on- and off-leash, polite greetings, crate training, bite inhibition, and confident socialization with other dogs and people.',
    meta: ['Ages 8 wks – 6 mo', '8 weeks', 'Classes of 4–5'],
    featured: true,
  },
  {
    tag: 'All breeds & ages',
    title: 'Rally Classes',
    interest: 'Rally',
    photo: '/img/class-rally.webp',
    body: 'A fast-paced, fun sport where you and your dog move together through a course of signs — building precision, focus, and teamwork at your own pace.',
    meta: ['Beginner friendly', 'Great next step'],
  },
  {
    tag: 'Novice → MOTCH',
    title: 'Competitive Obedience Classes',
    interest: 'Competitive Obedience',
    photo: '/img/class-obedience.jpg',
    body: 'Ongoing training for the ring — CKC, UKC and AKC. Develop the teamwork and precision that has produced more than 18 Obedience Trial Champions.',
    meta: ['All levels', 'Day / eve / weekend'],
  },
  {
    tag: 'Scent detection',
    title: 'Nose Work',
    interest: 'Nose Work',
    photo: '/img/class-nosework.jpg',
    body: 'Tap into your dog’s favourite sense. A rewarding, low-impact sport where dogs search out target scents — great for building focus and confidence in any dog.',
    meta: ['All ages', 'Low-impact'],
  },
];

const TRAINERS = [
  { name: 'Barry', role: 'Head Trainer · “The Dogfather”', photo: '/img/trainer-barry.jpg' },
  { name: 'Aislinn', role: 'Instructor', photo: '/img/trainer-ash.jpg' },
  { name: 'Lisa', role: 'Instructor', photo: '/img/trainer-lisa.jpg' },
  { name: 'Emilee', role: 'Instructor', photo: '/img/trainer-emilee.jpg' },
  { name: 'Dakotah', role: 'Instructor', photo: '/img/trainer-dakotah.jpg' },
  { name: 'Drey', role: 'Instructor', photo: '/img/trainer-drey.jpg' },
];

// Google reviews — google.com/maps/place/Top+Dogs+Training+Academy (4.9★, 129 reviews)
const TESTIMONIALS = [
  {
    quote:
      'I continue to be impressed by the progress my dog is making. With every class, I can see her skills improving and her confidence growing.',
    who: 'Erin Alary',
    dog: 'Google review',
  },
  {
    quote:
      'After that first set of classes, we have had Drey as our instructor. … From my dog’s perspective, he gets excited when he realizes we’re going to class, and Drey is the funnest person ever.',
    who: 'Tracy Marshall',
    dog: 'Google review',
  },
  {
    quote:
      'I can’t explain how amazing Emilee is. She is so knowledgeable, patient and understanding. She really takes her time to explain things and breaks them down.',
    who: 'Sara',
    dog: 'Google review, Local Guide',
  },
];

const GALLERY = [
  { src: '/img/grad-01.jpg', alt: 'Puppy class graduate with certificate' },
  { src: '/img/grad-02.jpg', alt: 'Puppy class graduate with certificate' },
  { src: '/img/grad-03.jpg', alt: 'Puppy class graduates with certificates' },
  { src: '/img/grad-04.jpg', alt: 'Puppy class graduates with certificates' },
  { src: '/img/grad-05.jpg', alt: 'Puppy class graduate with certificate' },
  { src: '/img/grad-06.jpg', alt: 'Puppy class graduate with certificate' },
  { src: '/img/grad-07.jpg', alt: 'Puppy class graduates with certificates' },
  { src: '/img/grad-08.jpg', alt: 'Puppy class graduates with certificates' },
  { src: '/img/grad-09.jpg', alt: 'Puppy class graduate with certificate' },
];

// Advanced/competitive obedience students showing their dogs and earning titles.
const TITLES = [
  { src: '/img/title-01.jpg', alt: 'Handler with dog and placement ribbons' },
  { src: '/img/title-02.jpg', alt: 'Handler with dog and placement ribbons' },
  { src: '/img/title-03.jpg', alt: 'Handlers with dogs and placement ribbons' },
  { src: '/img/title-04.jpg', alt: 'Handler with dog on the winner’s podium' },
  { src: '/img/title-05.jpg', alt: 'Handler with dog and placement ribbons' },
  { src: '/img/title-06.jpg', alt: 'Handlers with dogs and placement ribbons' },
  { src: '/img/title-07.jpg', alt: 'Handlers with dogs and placement ribbons' },
];

const MAP_QUERY = '20215 Vanneck Road, Komoka, Ontario';

// Opens the business's Google Maps listing, where all of our reviews live.
const GOOGLE_REVIEWS_URL = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
  'Top Dogs Training Academy, 20215 Vanneck Road, Komoka, Ontario',
)}`;

function Rosette() {
  // Champion rosette drawn inline so it scales crisply and matches the
  // gold accent (the club is "#1 voted in London").
  return (
    <svg viewBox="0 0 100 120" className="hero-rosette" role="img" aria-label="Voted #1 in London">
      <g transform="translate(50 44)">
        {Array.from({ length: 16 }).map((_, i) => (
          <ellipse
            key={i}
            cx="0"
            cy="-30"
            rx="7"
            ry="15"
            fill={i % 2 ? '#cf922b' : '#e6a836'}
            transform={`rotate(${i * 22.5})`}
          />
        ))}
        <circle r="24" fill="#7a4b2b" />
        <circle r="24" fill="none" stroke="#e6a836" strokeWidth="2.5" />
        <text
          y="-2"
          textAnchor="middle"
          fontFamily="Fraunces, serif"
          fontWeight="700"
          fontSize="20"
          fill="#fbe5c4"
        >
          #1
        </text>
        <text
          y="12"
          textAnchor="middle"
          fontFamily="Hanken Grotesk, sans-serif"
          fontWeight="700"
          fontSize="7"
          letterSpacing="1"
          fill="#e6a836"
        >
          LONDON
        </text>
      </g>
      <path d="M38 66 L34 104 L50 92 L66 104 L62 66 Z" fill="#c15f39" />
    </svg>
  );
}

// Paw-print silhouette, used for the floating hero decorations and the
// big contact-section watermark. Colored via currentColor.
function Paw({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 100 100" aria-hidden="true" focusable="false">
      <g fill="currentColor">
        <ellipse cx="22" cy="36" rx="10" ry="13" transform="rotate(-18 22 36)" />
        <ellipse cx="41" cy="24" rx="10" ry="14" transform="rotate(-6 41 24)" />
        <ellipse cx="61" cy="24" rx="10" ry="14" transform="rotate(6 61 24)" />
        <ellipse cx="80" cy="36" rx="10" ry="13" transform="rotate(18 80 36)" />
        <path d="M51 47c15 0 27 10 27 23 0 10-8 17-27 17S24 80 24 70c0-13 12-23 27-23z" />
      </g>
    </svg>
  );
}

// Rotating "EST. 1975" ink stamp for the hero collage.
function Stamp() {
  return (
    <svg className="hero-stamp" viewBox="0 0 120 120" aria-hidden="true" focusable="false">
      <circle cx="60" cy="60" r="57" fill="#fdf8ef" stroke="#7a4b2b" strokeWidth="2.5" />
      <circle
        cx="60"
        cy="60"
        r="40"
        fill="none"
        stroke="#7a4b2b"
        strokeWidth="1"
        strokeDasharray="3 4"
      />
      <g className="stamp-rotor">
        <defs>
          <path id="stamp-arc" d="M60,60 m-48,0 a48,48 0 1,1 96,0 a48,48 0 1,1 -96,0" />
        </defs>
        <text
          fontFamily="Hanken Grotesk, sans-serif"
          fontWeight="800"
          fontSize="10.5"
          letterSpacing="1.6"
          fill="#7a4b2b"
        >
          <textPath href="#stamp-arc">TOP DOGS ACADEMY · EST. 1975 · KOMOKA, ONT ·</textPath>
        </text>
      </g>
      <g transform="translate(38 39) scale(0.44)" color="#cf922b">
        <Paw />
      </g>
    </svg>
  );
}

// Scalloped section edge — echoes the rosette ribbons. Absolutely
// positioned at the top of a section, overlapping the one above.
function Scallop({ fill }: { fill: string }) {
  const bumps = Array.from({ length: 30 }, () => 'a20,9 0 0 1 40,0').join(' ');
  return (
    <svg
      className="scallop"
      viewBox="0 0 1200 28"
      preserveAspectRatio="none"
      aria-hidden="true"
      focusable="false"
    >
      <path d={`M0,28 L0,16 ${bumps} L1200,28 Z`} fill={fill} />
    </svg>
  );
}

export function SplashPage() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Reveal-on-scroll for elements marked `.reveal`.
  useEffect(() => {
    const els = Array.from(document.querySelectorAll('.splash .reveal'));
    if (!('IntersectionObserver' in window)) {
      els.forEach((el) => el.classList.add('in'));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('in');
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.14 },
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  const close = () => setMenuOpen(false);

  return (
    <div className="splash">
      <div className="splash-bg" aria-hidden />

      {/* ── Nav ── */}
      <nav className={`s-nav${scrolled ? ' scrolled' : ''}`}>
        <a href="#top" className="s-brand" onClick={close}>
          <img src="/img/logo.png" alt="" />
          <span>
            Top Dogs
            <small>Training Academy</small>
          </span>
        </a>
        <div className={`s-nav-links${menuOpen ? ' open' : ''}`}>
          <a className="text-link" href="#classes" onClick={close}>
            Classes
          </a>
          <a className="text-link" href="#about" onClick={close}>
            About
          </a>
          <a className="text-link" href="#trainers" onClick={close}>
            Trainers
          </a>
          <a className="text-link" href="#reviews" onClick={close}>
            Reviews
          </a>
          <Link className="text-link" to="/login" onClick={close}>
            Staff login
          </Link>
        </div>
        <button className="s-nav-toggle" aria-label="Menu" onClick={() => setMenuOpen((o) => !o)}>
          {menuOpen ? '✕' : '☰'}
        </button>
      </nav>

      <main className="splash-main" id="top">
        {/* ── Hero ── */}
        <section className="hero">
          <div className="hero-paws" aria-hidden>
            <span className="hp hp1">
              <Paw />
            </span>
            <span className="hp hp2">
              <Paw />
            </span>
            <span className="hp hp3">
              <Paw />
            </span>
          </div>
          <div className="wrap-x hero-grid">
            <div className="hero-copy">
              <span className="eyebrow hero-anim d1">Komoka · London, Ontario</span>
              <h1 className="hero-anim d1">
                Raising London’s{' '}
                <span className="squiggle-wrap">
                  <span className="italic-accent">best-behaved</span>
                  <svg
                    className="squiggle"
                    viewBox="0 0 220 14"
                    preserveAspectRatio="none"
                    aria-hidden="true"
                    focusable="false"
                  >
                    <path
                      d="M4 9 C 30 3, 55 13, 82 8 S 135 2, 162 9 S 204 12, 216 5"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="4.5"
                      strokeLinecap="round"
                    />
                  </svg>
                </span>
                <span className="line2">dogs since 1975.</span>
              </h1>
              <p className="hero-sub hero-anim d2">
                We don’t just train dogs — we train <strong>you</strong> to train your dog. Fifty
                years of all-positive methods, small classes, and guaranteed results.
              </p>
              <div className="hero-cta hero-anim d3">
                <a className="s-btn s-btn-primary" href="#classes">
                  Book a class →
                </a>
                <a className="s-btn s-btn-ghost" href="#classes">
                  See our classes
                </a>
                <a className="s-btn s-btn-ghost" href="#reviews">
                  See our reviews
                </a>
              </div>
              <div className="hero-stats hero-anim d4">
                <div className="hero-stat">
                  <div className="n">50 yrs</div>
                  <div className="l">Of positive training</div>
                </div>
                <div className="hero-stat">
                  <div className="n">#1</div>
                  <div className="l">Voted best in London</div>
                </div>
                <div className="hero-stat">
                  <div className="n">18+</div>
                  <div className="l">Obedience champions</div>
                </div>
                <div className="hero-stat">
                  <div className="n">4–5</div>
                  <div className="l">Dogs per class</div>
                </div>
              </div>
            </div>

            <div className="hero-art" aria-hidden>
              <Rosette />
              <Stamp />
              <div className="frame f1">
                <img
                  src="/img/hero-client3.jpg"
                  alt="Client with their dog and certificate"
                  style={{ objectPosition: 'center 24%' }}
                />
              </div>
              <div className="frame f2">
                <img
                  src="/img/hero-client2.jpg"
                  alt="Client with their dog and certificate"
                  style={{ objectPosition: 'center 15%' }}
                />
              </div>
              <div className="frame f3">
                <img
                  src="/img/hero-client1.jpg"
                  alt="Client with their dog and award ribbons"
                  style={{ objectPosition: 'center 0%' }}
                />
              </div>
            </div>
          </div>
        </section>

        {/* ── Trust marquee ── */}
        <div className="marquee" aria-hidden>
          <div className="marquee-track">
            {[0, 1].map((k) => (
              <span key={k}>
                All-Positive Methods
                <span>Recommended by Veterinarians</span>
                <span>Trusted Since 1975</span>
                <span>Multi-Award Winning</span>
                <span>Small Class Sizes</span>
              </span>
            ))}
          </div>
        </div>

        {/* ── Promo video ── */}
        <section className="section-pad" style={{ paddingBottom: 0 }}>
          <div className="wrap-x">
            <div className="section-head center reveal">
              <span className="eyebrow">See us in action</span>
              <h2 className="section-title">
                Watch the <em className="italic-accent">Top Dogs</em> story
              </h2>
            </div>
            <div className="video-frame reveal">
              <iframe
                src="https://www.youtube.com/embed/0a7dVg2-iUY"
                title="Top Dogs Training Academy promotional video"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                loading="lazy"
              />
            </div>
          </div>
        </section>

        {/* ── About ── */}
        <section className="section-pad" id="about">
          <div className="wrap-x about-grid">
            <div className="about-photo reveal">
              <img src="/img/trainer-barry.jpg" alt="Barry with a client dog and award ribbon" />
              <div className="highlight-card">
                <div className="big">Since 1975</div>
                <div>Fifty years of raising London's best-behaved dogs.</div>
              </div>
            </div>
            <div className="about-body reveal">
              <span className="eyebrow">Meet the Dogfather</span>
              <h2 className="section-title">
                Kind methods.
                <br />
                Serious results.
              </h2>
              <p className="section-lead" style={{ marginTop: '1.1rem' }}>
                For five decades, head trainer <strong>Barry</strong> — affectionately known as “The
                Dogfather” — has helped thousands of London families raise happy, confident,
                well-mannered dogs. Every class is built on positive reinforcement, never fear.
              </p>
              <ul className="checklist">
                <li>All-positive reinforcement — we build trust, not fear</li>
                <li>Small classes of 4–5 dogs for real, personal attention</li>
                <li>Highly recommended by local veterinarians</li>
              </ul>
              <p className="about-sign">“We train people to train their dogs.”</p>
            </div>
          </div>
        </section>

        {/* ── Classes ── */}
        <section className="section-pad" id="classes" style={{ scrollMarginTop: 80 }}>
          <div className="wrap-x">
            <div className="section-head center reveal">
              <span className="eyebrow">What we offer</span>
              <h2 className="section-title">
                Classes for <em className="italic-accent">every stage</em>
              </h2>
              <p className="section-lead" style={{ margin: '0.8rem auto 0' }}>
                From a brand-new puppy to the competition ring — small, friendly classes seven days
                a week.
              </p>
            </div>
            <div className="class-grid">
              {CLASSES.map((c, i) => (
                <article
                  className={`class-card reveal${c.featured ? ' featured' : ''}`}
                  key={c.title}
                  style={{ transitionDelay: `${i * 0.08}s` }}
                >
                  <div className="class-card-photo">
                    <img src={c.photo} alt={c.title} loading="lazy" />
                  </div>
                  <div className="class-card-body">
                    <span className="class-num" aria-hidden>
                      0{i + 1}
                    </span>
                    <span className="class-tag">{c.tag}</span>
                    <h3>{c.title}</h3>
                    <p>{c.body}</p>
                    <div className="class-meta">
                      {c.meta.map((m) => (
                        <span key={m}>{m}</span>
                      ))}
                    </div>
                    <div className="class-card-actions">
                      <a className="s-btn s-btn-ghost class-card-call" href="#contact">
                        📞 Call us
                      </a>
                      <Link
                        className="s-btn s-btn-ghost class-card-inquire"
                        to={`/inquire?class=${encodeURIComponent(c.interest)}`}
                      >
                        Inquire →
                      </Link>
                    </div>
                  </div>
                </article>
              ))}
            </div>
            <p className="class-extra reveal">
              Also offering an <strong>Advanced Workshop</strong>, <strong>Basic Manners</strong>,
              and <strong>private sessions</strong>. Not sure where to start?{' '}
              <a href="#contact">We’ll help you choose.</a>
            </p>
          </div>
        </section>

        {/* ── Awards band ── */}
        <section className="awards" id="awards">
          <Scallop fill="#2c1c10" />
          <div className="wrap-x">
            <div className="reveal">
              <span className="eyebrow">A wall full of proof</span>
              <h2>
                Champions are <em className="gold-italic">made</em> here.
              </h2>
              <p>
                Half a century of ribbons, titles, and happy graduates. Our students have earned
                more than <strong>18 Obedience Trial Champions (OTCH)</strong> and many Master
                Obedience Trial Champions — and we’re proud to be voted London’s #1 dog training
                club.
              </p>
              <div className="awards-stats">
                <div>
                  <div className="n">18+</div>
                  <div className="l">OTCH champions</div>
                </div>
                <div>
                  <div className="n">50</div>
                  <div className="l">Years teaching</div>
                </div>
                <div>
                  <div className="n">#1</div>
                  <div className="l">Voted in London</div>
                </div>
              </div>
            </div>
            <div className="awards-photo reveal">
              <img src="/img/award-ribbons.jpg" alt="A wall of competition ribbons and rosettes" />
            </div>
          </div>
        </section>

        {/* ── Titled achievements ── */}
        <section className="section-pad" id="titles">
          <div className="wrap-x">
            <div className="section-head center reveal">
              <span className="eyebrow">Advanced achievements</span>
              <h2 className="section-title">
                Titled in the <em className="italic-accent">ring</em>
              </h2>
              <p className="section-lead" style={{ margin: '0.8rem auto 0' }}>
                Our competitive obedience students, out showing their dogs and earning real titles.
              </p>
            </div>
            <div className="gallery reveal">
              {TITLES.map((g) => (
                <figure className="snap" key={g.src}>
                  <img src={g.src} alt={g.alt} loading="lazy" />
                </figure>
              ))}
            </div>
          </div>
        </section>

        {/* ── Trainers ── */}
        <section className="section-pad" id="trainers">
          <div className="wrap-x">
            <div className="section-head center reveal">
              <span className="eyebrow">Your team</span>
              <h2 className="section-title">
                Meet the <em className="italic-accent">trainers</em>
              </h2>
              <p className="section-lead" style={{ margin: '0.8rem auto 0' }}>
                Straight from our wall of ribbons — every trainer competes with (and is trained by)
                their own dogs.
              </p>
            </div>
            <div className="trainer-grid">
              {TRAINERS.map((t, i) => (
                <div
                  className="trainer reveal"
                  key={t.name}
                  style={{ transitionDelay: `${i * 0.06}s` }}
                >
                  <figure className="polaroid">
                    <span className="tape" aria-hidden />
                    <div className="polaroid-photo">
                      <img src={t.photo} alt={t.name} loading="lazy" />
                    </div>
                    <figcaption>
                      <h3>{t.name}</h3>
                      <div className="role">{t.role}</div>
                    </figcaption>
                  </figure>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Testimonials ── */}
        <section className="section-pad testimonials" id="reviews">
          <Scallop fill="#f5ead8" />
          <div className="wrap-x">
            <div className="section-head center reveal">
              <span className="eyebrow">Happy tails</span>
              <h2 className="section-title">
                <em className="italic-accent">Loved</em> by London dog owners
              </h2>
              <p className="section-lead" style={{ margin: '0.8rem auto 0' }}>
                4.9★ average from 129 reviews —{' '}
                <a
                  className="google-link"
                  href={GOOGLE_REVIEWS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  read them all on Google
                </a>
              </p>
            </div>
            <div className="tcols">
              {TESTIMONIALS.map((t, i) => (
                <figure
                  className={`tcard reveal${i === 0 ? ' featured' : ''}`}
                  key={t.who + t.quote.slice(0, 12)}
                >
                  <div className="paws-rating" aria-label="Rated 5 out of 5">
                    {Array.from({ length: 5 }).map((_, k) => (
                      <Paw key={k} />
                    ))}
                  </div>
                  <blockquote>“{t.quote}”</blockquote>
                  <figcaption className="who">
                    {t.who}
                    {t.dog && <span> — {t.dog}</span>}
                  </figcaption>
                </figure>
              ))}
            </div>
            <p className="reviews-more reveal">
              <a
                className="s-btn s-btn-ghost"
                href={GOOGLE_REVIEWS_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                See all our Google reviews →
              </a>
            </p>
          </div>
        </section>

        {/* ── Gallery ── */}
        <section className="section-pad" id="gallery" style={{ paddingTop: 0 }}>
          <div className="wrap-x">
            <div className="section-head center reveal">
              <span className="eyebrow">Good dogs</span>
              <h2 className="section-title">
                Our <em className="italic-accent">graduates</em>
              </h2>
            </div>
            <div className="gallery reveal">
              {GALLERY.map((g) => (
                <figure className="snap" key={g.src}>
                  <img src={g.src} alt={g.alt} loading="lazy" />
                </figure>
              ))}
            </div>
          </div>
        </section>

        {/* ── Contact ── */}
        <section className="section-pad contact" id="contact">
          <Scallop fill="#2c1c10" />
          <Paw className="contact-paw" />
          <div className="wrap-x contact-grid">
            <div className="reveal">
              <span className="eyebrow">Come say hi</span>
              <h2>
                Ready to get <em className="gold-italic">started?</em>
              </h2>
              <p className="contact-lead">
                New classes start regularly and spots fill fast. Give us a call and we’ll get you
                and your dog into the right class.
              </p>
              <ul className="contact-list">
                <li>
                  <span className="ico">📞</span>
                  <span>
                    <span className="k">Call to register</span>
                    <a className="v bigphone" href="tel:+15194736156">
                      519.473.6156
                    </a>
                  </span>
                </li>
                <li>
                  <span className="ico">✉️</span>
                  <span>
                    <span className="k">Email us</span>
                    <a className="v" href="mailto:hello@topdogsacademy.ca">
                      hello@topdogsacademy.ca
                    </a>
                  </span>
                </li>
                <li>
                  <span className="ico">📍</span>
                  <span>
                    <span className="k">Find us</span>
                    <span className="v">20215 Vanneck Road, Komoka, ON</span>
                    <div style={{ color: '#d8c4a3', fontSize: '0.9rem', marginTop: 2 }}>
                      Just 5 minutes from Hyde Park Rd &amp; Gainsborough Rd
                    </div>
                  </span>
                </li>
              </ul>
              <div className="contact-cta">
                <a className="s-btn s-btn-gold" href="tel:+15194736156">
                  Call now
                </a>
                <a
                  className="s-btn s-btn-ghost"
                  style={{ color: '#fbefd9', borderColor: '#e6a836' }}
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(MAP_QUERY)}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  Get directions
                </a>
              </div>
            </div>
            <div className="map-card reveal">
              <iframe
                title="Top Dogs Training Academy location"
                src={`https://www.google.com/maps?q=${encodeURIComponent(MAP_QUERY)}&output=embed`}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>
        </section>

        {/* ── Footer ── */}
        <footer className="s-footer">
          <div className="wrap-x">
            <a href="#top" className="s-brand">
              <img src="/img/logo.png" alt="" />
              <span>
                Top Dogs
                <small>Training Academy</small>
              </span>
            </a>
            <nav className="s-footer-nav">
              <a href="#classes">Classes</a>
              <a href="#about">About</a>
              <a href="#trainers">Trainers</a>
              <a href="#reviews">Reviews</a>
              <a href="#contact">Contact</a>
              <Link to="/login">Staff login</Link>
            </nav>
          </div>
          <div className="wrap-x s-footer-bottom">
            <span>© {new Date().getFullYear()} Top Dogs Training Academy · Komoka, Ontario</span>
            <span>Positive training since 1975 🐾</span>
          </div>
        </footer>
      </main>
    </div>
  );
}
